/*
  The anonymous id a player's plays are keyed by, and what happens to it when they sign out.

  Each client stores it differently — the website in the `inkwell-user` cookie, the app in its own
  storage, since it has no cookie jar — but the rule for stepping off an account is the same in both,
  and the server reads the result. So the rule lives here.
*/

/**
 * Marks an id as belonging to a signed-out player.
 *
 * The server knows this prefix: signing in from an id that carries it never adopts that id as the
 * account's primary, it mints a fresh one. That is what keeps a shared device from folding the next
 * person's plays into the last person's account.
 */
export const LOGGED_OUT_USER_STR_PREFIX = 'loggedout';

/** Whether this id belongs to a player who signed out. */
export const isLoggedOutUserStr = (userStr: string): boolean => userStr.startsWith(`${LOGGED_OUT_USER_STR_PREFIX}_`);

/**
 * The id to carry after signing out — derived from the current one rather than minted, so a player
 * who signs out keeps one identity across sign-outs and the plays they make while signed out are
 * still theirs on the way back.
 *
 * Already-marked ids are returned unchanged. Signing out twice without signing in between is not the
 * usual path, but an expired refresh token takes it, and nesting the prefix would strand whatever
 * had been recorded against the id before.
 */
export const loggedOutUserStr = (userStr: string): string =>
  isLoggedOutUserStr(userStr) ? userStr : `${LOGGED_OUT_USER_STR_PREFIX}_${userStr}`;
